import {EditorContent, EditorEvents, useEditor} from '@tiptap/react';
import styles from './TiptapEditor.module.scss';
import {HandleRtfInputChange, SetValidationMessages} from '../../../types/form-types';
import {useEffect, useMemo, useState} from 'react';
import {
  addOrRemoveValidationMessage,
  convertCharsToKB,
  debounce_delay_ms,
} from '../../../lib/utils';
import {parseProsemirrorState} from '../../../../common/utils/rtf-utils';
import StarterKit from '@tiptap/starter-kit';
import {Color, TextStyle} from '@tiptap/extension-text-style';
import OfficePaste from '@csnw/tiptap-extension-office-paste';
import TiptapToolbar from '../TiptapToolbar/TiptapToolbar';
import {useDebouncedCallback} from 'use-debounce';
import SectionDescription from '../../FormComponents/SectionDescription/SectionDescription';

type MaxLengthValidation =
  | {max_length: number; max_length_validation_message: string}
  | {max_length?: never; max_length_validation_message?: never};

type RequiredValidation =
  | {is_required: boolean; required_validation_message: string}
  | {is_required?: never; required_validation_message?: never};

export type TiptapEditorProps = {
  id: string;
  title: string;
  handleChange: HandleRtfInputChange;
  setValidationMessages: SetValidationMessages;
  initial_content?: string;
} & RequiredValidation &
  MaxLengthValidation;

const StarterKitConfigured = StarterKit.configure({
  link: {openOnClick: false},
});

// Extensions that affect Prosemirror schema should also be added to src/common/utils/rtf-utils.ts.
// Extensions that affect rendering should also be added to src/client/components/Tiptap/TiptapRender/TiptapRender.tsx.
const extensions = [StarterKitConfigured, Color, TextStyle, OfficePaste];

export default function TiptapEditor({
  id,
  title,
  handleChange,
  setValidationMessages,
  initial_content,
  is_required,
  required_validation_message,
  max_length,
  max_length_validation_message,
}: TiptapEditorProps) {
  const [isValid, setIsValid] = useState(true);
  const [contentSize, setContentSize] = useState(initial_content?.length ?? 0);

  const onUpdate = useDebouncedCallback((update: EditorEvents['update']) => {
    const json = JSON.stringify(update.editor.getJSON());
    handleChange(id, json);
    setContentSize(json.length);
    // eslint-disable-next-line no-use-before-define
    setHasText(!!update.editor.getText().length);
  }, debounce_delay_ms);

  const editor = useEditor({
    extensions,
    content: parseProsemirrorState(initial_content ?? ''),
    onUpdate,
    editorProps: {
      attributes: {id},
    },
    onContentError: (err) => {
      if (g_is_dev) console.error(err);
    },
  });

  const [hasText, setHasText] = useState(!!editor.getText().length);
  const size_remaining = useMemo(
    () =>
      max_length != undefined
        ? convertCharsToKB(max_length) - convertCharsToKB(contentSize)
        : undefined,
    [contentSize, max_length]
  );

  useEffect(() => {
    const is_required_valid = !is_required || hasText;
    addOrRemoveValidationMessage({
      is_condition_met: !is_required_valid,
      err_message: required_validation_message ?? 'Value is required',
      setValidationMessages,
    });

    const is_max_length_valid = max_length == undefined || contentSize <= max_length;
    addOrRemoveValidationMessage({
      is_condition_met: !is_max_length_valid,
      err_message: max_length_validation_message ?? 'Value is too long',
      setValidationMessages,
    });

    setIsValid(is_required_valid && is_max_length_valid);
  }, [
    is_required,
    setValidationMessages,
    required_validation_message,
    max_length,
    contentSize,
    max_length_validation_message,
    hasText,
  ]);

  return (
    <>
      <div className={`margin-top-3 ${styles.rtf_editor} ${!isValid ? styles.rtf_error : ''}`}>
        <p className="text-semibold margin-bottom-05 text-primary">
          <span
            className={styles.label}
            tabIndex={-1}
            role="button"
            onClick={() => editor.commands.focus()}
            onKeyUp={() => undefined}
          >
            {title}
            {is_required && ' *'}
          </span>
        </p>
        <TiptapToolbar editor={editor} />
        <EditorContent editor={editor} />
      </div>
      {size_remaining != undefined && (
        <SectionDescription is_error={size_remaining < 0}>
          {size_remaining.toFixed(3)} KB remaining (number of characters and use of rich text affect
          this)
        </SectionDescription>
      )}
    </>
  );
}
